import isEqual from 'lodash/isEqual';
import { useMemo } from 'react';

import {
  type DataProviderChildren,
  DataProviderQueryStatus,
} from '../../next/CustomDash/components/DataProvider/types';
import { useUserPreferences } from './hooks/useUserPreferences';
import type { PreferenceScope, UserPreferences } from './types';
import { deriveEffectiveTaxonomiesForScope } from './utils/derive-effective-taxonomies';

export interface UserPreferencesData {
  scope: PreferenceScope;
  savedPreferences: UserPreferences;
  preferences: UserPreferences;
  /** Re-sync saved preferences from the server using current local storage state */
  refetchSavedPreferences: () => Promise<void>;
}

export const UserPreferencesDataProvider = ({
  affectedScope,
  preferencesFromFilters,
  children,
}: {
  affectedScope: PreferenceScope;
  preferencesFromFilters?: Partial<UserPreferences>; // allows preferences controlled by filters to modify preference editor values
  children: DataProviderChildren<UserPreferencesData>;
}) => {
  const {
    preferences,
    savedPreferences,
    isInitialLoading,
    error,
    syncWithServer,
  } = useUserPreferences(affectedScope);

  const data = useMemo<UserPreferencesData>(() => {
    const storedTaxonomies = preferences?.taxonomies;
    const filterTaxonomies = preferencesFromFilters?.taxonomies;
    const derivedStored = deriveEffectiveTaxonomiesForScope(
      storedTaxonomies,
      affectedScope
    );

    const mergedTaxonomies = {
      ...(storedTaxonomies || {}),
      ...(filterTaxonomies || {}),
    };
    // A filter value that merely echoes the derived global choice (adaptive
    // materialized as Generic on a non-company page) is not a user override:
    // keep displaying/diffing the raw stored choice.
    if (
      filterTaxonomies?.role !== undefined &&
      isEqual(filterTaxonomies.role, derivedStored?.role)
    ) {
      mergedTaxonomies.role = storedTaxonomies?.role;
    }
    if (
      filterTaxonomies?.skill !== undefined &&
      isEqual(filterTaxonomies.skill, derivedStored?.skill)
    ) {
      mergedTaxonomies.skill = storedTaxonomies?.skill;
    }
    if (
      filterTaxonomies?.activity !== undefined &&
      isEqual(filterTaxonomies.activity, derivedStored?.activity)
    ) {
      mergedTaxonomies.activity = storedTaxonomies?.activity;
    }

    return {
      scope: affectedScope,
      savedPreferences,
      // Prefer the actual filter-derived values for display, so editors reflect
      // what the page is currently using.
      preferences: {
        ...preferences,
        ...preferencesFromFilters,
        taxonomies: mergedTaxonomies,
      },
      refetchSavedPreferences: syncWithServer,
    };
  }, [
    affectedScope,
    preferences,
    savedPreferences,
    preferencesFromFilters,
    syncWithServer,
  ]);

  let status = DataProviderQueryStatus.Success;
  if (isInitialLoading) status = DataProviderQueryStatus.Fetching;
  if (error) status = DataProviderQueryStatus.Error;

  return children({ data, status });
};
